import React, { useState } from "react";
import ReactTable from "react-table";
import { useTranslation } from "react-i18next";
import range from "lodash/range";

import { translations } from "helpers/react-table";

const getPageSizeOptions = (total) =>
  [10, 15].concat(
    range(7)
      .map((i) => i * 5 + 20)
      .filter((n) => n <= total),
  );

export const EtudeResultTable = ({ study, isFetching }) => {
  const { t } = useTranslation();
  const ref = React.useRef(null);
  const [selectedRows, setSelectedRows] = useState({});

  const maxPeriods = React.useMemo(() => {
    let max = 5; // R1 ~ R5
    study?.EtudeResults?.forEach((row) => {
      const initialLen = row.InitialSubscription?.Puissance?.length || 0;
      const targetLen = row.TargetSubscription?.Puissance?.length || 0;
      if (initialLen > max) max = initialLen;
      if (targetLen > max) max = targetLen;
    });
    return max;
  }, [study]);

  const tableData = React.useMemo(() => {
    if (!study || !study.EtudeResults || study.EtudeResults.length === 0) {
      return [];
    }

    return study.EtudeResults.map((row) => {
      const formattedRow = {
        PeeClef: row.PeeClef,
        Volume: row.Volume,
        DeltaCout: row.DeltaCout,
        InitialTarifRegule: row.InitialSubscription?.TarifRegule || "-",
        InitialCout: row.InitialSubscription?.Cout,
        TargetTarifRegule: row.TargetSubscription?.TarifRegule || "-",
        TargetCout: row.TargetSubscription?.Cout,
      };

      // Key is like initial_r_0, target_r_0...
      for (let i = 0; i < maxPeriods; i++) {
        formattedRow[`initial_r_${i}`] =
          row.InitialSubscription?.Puissance?.[i] ?? null;
        formattedRow[`target_r_${i}`] =
          row.TargetSubscription?.Puissance?.[i] ?? null;
      }

      return formattedRow;
    });
  }, [study, maxPeriods]);

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "-";
    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const columns = React.useMemo(() => {
    // Generate R1, R2, R3... columns based on maxPeriods
    const generateRColumns = (prefix) =>
      Array.from({ length: maxPeriods }, (_, i) => ({
        Header: `R${i + 1}`,
        // Accessor key must match the one we created in tableData, e.g. initial_r_0, target_r_0...
        accessor: `${prefix}_r_${i}`,
        Cell: ({ value }) => (value !== null ? formatNumber(value) : "-"),
        width: 70,
      }));

    return [
      {
        Header: t("etudes.resultTable.meter") || "PDL",
        accessor: "PeeClef",
        Cell: ({ value }) =>
          value === "Total" ? <strong>{value}</strong> : value,
        width: 130,
      },
      {
        Header: t("etudes.resultTable.volume") || "Volume (MWh)",
        accessor: "Volume",
        Cell: ({ value }) => formatNumber(value),
        width: 110,
      },
      // Initial Subscription Group
      {
        Header:
          t("etudes.resultTable.initialSubscription") || "Initial Subscription",
        headerStyle: {
          backgroundColor: "var(--primary)",
          fontWeight: "bold",
          borderBottom: "1px solid #cbd5e1",
          color: "#fff",
        },
        columns: [
          {
            Header: t("etudes.resultTable.regularTarif") || "Tarif regulé",
            accessor: "InitialTarifRegule",
            width: 160,
          },
          ...generateRColumns("initial"),
          {
            Header: t("etudes.resultTable.cost") || "Coût (€)",
            accessor: "InitialCout",
            Cell: ({ value }) => formatNumber(value),
            width: 100,
          },
        ],
      },
      // Target Subscription Group
      {
        Header:
          t("etudes.resultTable.targetSubscription") || "Target Subscription",
        headerStyle: {
          backgroundColor: "var(--primary)",
          fontWeight: "bold",
          borderBottom: "1px solid #cbd5e1",
          borderLeft: "2px solid #ffffff",
          color: "#fff",
        },
        columns: [
          {
            Header: t("etudes.resultTable.regularTarif") || "Tarif regulé",
            accessor: "TargetTarifRegule",
            width: 160,
          },
          ...generateRColumns("target"),
          {
            Header: t("etudes.resultTable.cost") || "Coût (€)",
            accessor: "TargetCout",
            Cell: ({ value }) => formatNumber(value),
            width: 100,
          },
        ],
      },
      {
        Header: t("etudes.resultTable.difference") || "Delta (€)",
        accessor: "DeltaCout",
        Cell: ({ value }) => formatNumber(value),
        width: 100,
      },
    ];
  }, [t, maxPeriods]);

  return (
    <div style={{ marginTop: "30px" }}>
      <h4>
        {study?.Name
          ? `${t("etudes.resultTable.title")} : ${study.Name}`
          : "Etude Details"}
      </h4>
      <ReactTable
        data={tableData || []}
        columns={columns}
        defaultPageSize={10}
        showPagination={tableData.length > 10}
        pageSizeOptions={getPageSizeOptions(tableData.length)}
        loading={isFetching}
        className="-striped -highlight"
        // Change row style for Total row
        getTrProps={(state, rowInfo) => {
          if (
            rowInfo &&
            rowInfo.original &&
            rowInfo.original.PeeClef === "Total"
          ) {
            return {
              style: {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                fontWeight: "bold",
              },
            };
          }
          return {};
        }}
      />
    </div>
  );
};
